import React, {Component} from 'react';
import PropTypes from 'prop-types';
import PureRenderMixin from 'react-addons-pure-render-mixin';
import classNames from 'classnames';
import DateUtils from 'date-utils';
import TimeUtils from 'time-utils';
import SHGlobals from 'sh-globals';
import DateTimePicker from 'date-time-picker-component';

const TOOLTIP_TYPES = ['date', 'time', 'none'];

class DateTimeRangePicker extends Component {
    constructor(props) {
        super(props);

        const {
            startDate,
            startTime,
            endDate,
            endTime,
            startTooltipType,
            startTooltipMessage,
            endTooltipType,
            endTooltipMessage
        } = props;

        this._IS_NATIVE_DATE_INPUT = SHGlobals.isMobile && DateUtils.supportsDateInput();
        this._DATE_FORMAT = (this._IS_NATIVE_DATE_INPUT) ? SHGlobals.MOBILE_DATE_FORMAT : SHGlobals.DESKTOP_DATE_FORMAT;
        this._startChange = this._startChange.bind(this);
        this._endChange = this._endChange.bind(this);
        this._startTooltipHidden = this._startTooltipHidden.bind(this);
        this._endTooltipHidden = this._endTooltipHidden.bind(this);

        this.state = {
            startDate: (this._IS_NATIVE_DATE_INPUT) ? DateUtils.convertToMobile(startDate) : startDate,
            startTime,
            endDate: (this._IS_NATIVE_DATE_INPUT) ? DateUtils.convertToMobile(endDate) : endDate,
            endTime,
            addHighlightClass: false,
            addErrorClass: false,
            startTipType: startTooltipType,
            startTipMessage: startTooltipMessage,
            endTipType: endTooltipType,
            endTipMessage: endTooltipMessage
        };
        this.shouldComponentUpdate = PureRenderMixin.shouldComponentUpdate.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        const {
            startDate,
            startTime,
            endDate,
            endTime
        } = this.state;
        const {
            startDate: newStartDate,
            startTime: newStartTime,
            endDate: newEndDate,
            endTime: newEndTime,
            startTooltipType,
            startTooltipMessage,
            endTooltipType,
            endTooltipMessage
        } = nextProps;
        const nextStartDate = (this._IS_NATIVE_DATE_INPUT) ? DateUtils.convertToMobile(newStartDate) : newStartDate;
        const nextEndDate = (this._IS_NATIVE_DATE_INPUT) ? DateUtils.convertToMobile(newEndDate) : newEndDate;
        const startUpdated = nextStartDate !== startDate || newStartTime !== startTime;
        const endUpdated = nextEndDate !== endDate || newEndTime !== endTime;
        const newState = {};

        if (startUpdated && endUpdated) {
            // if both have been updated, update complete state
            newState.startDate = nextStartDate;
            newState.startTime = newStartTime;
            newState.endDate = nextEndDate;
            newState.endTime = newEndTime;
            newState.addHighlightClass = false;
            newState.addErrorClass = false;
            newState.startTipType = startTooltipType;
            newState.startTipMessage = startTooltipMessage;
            newState.endTipType = endTooltipType;
            newState.endTipMessage = endTooltipMessage;
        } else if (startUpdated) {
            // only start has been updated
            newState.startDate = nextStartDate;
            newState.startTime = newStartTime;
            newState.startTipType = startTooltipType;
            newState.startTipMessage = startTooltipMessage;
        } else if (endUpdated) {
            // only end has been updated
            newState.endDate = nextEndDate;
            newState.endTime = newEndTime;
            newState.endTipType = endTooltipType;
            newState.endTipMessage = endTooltipMessage;
        }

        this.setState(newState);
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            onInvalid
        } = this.props;
        const {
            startDate,
            startTime,
            endDate,
            endTime,
            addHighlightClass
        } = this.state;
        let isValid = true;

        if (addHighlightClass) {
            // if the previous state of the component included the highlight class remove it here
            // so that next time it needs to highlight it can be re-added and the animation plays
            if (this._removeHighlightTimeout) {
                clearTimeout(this._removeHighlightTimeout);
            }

            this._removeHighlightTimeout = setTimeout(() => {
                this.setState({
                    addHighlightClass: false
                });
            }, 3000);
        }

        if ((startDate !== prevState.startDate || startTime !== prevState.startTime) &&
            (startDate && startTime && endDate && endTime)) {
            if (this._checkIfStartIsAfterEnd()) {
                isValid = false;
            }

        }

        if ((endDate !== prevState.endDate || endTime !== prevState.endTime) &&
            (startDate && startTime && endDate && endTime)) {
            if (this._checkIfEndIsBeforeStart()) {
                isValid = false;
            }
        }

        if (onInvalid && !isValid) {
            onInvalid();
        }
    }

    componentWillUnmount() {
        if (this._removeHighlightTimeout) {
            clearTimeout(this._removeHighlightTimeout);
        }
    }

    _getCombinedDate(date, time) {
        return DateUtils.combineDateAndTime(DateUtils.getAsMoment(date), TimeUtils.getAsMoment(time));
    }

    _checkIfStartIsAfterEnd() {
        const {
            endTimeBump
        } = this.props;
        const {
            startDate,
            startTime,
            endDate,
            endTime
        } = this.state;
        const start = this._getCombinedDate(startDate, startTime);
        const end = this._getCombinedDate(endDate, endTime);
        const newState = {};
        let isInvalid = false;

        if (!end || end.isBefore(start) || end.isSame(start)) {
            const newEnd = (!end || end.hours() < start.hours())
                ? start.clone().add(endTimeBump, 'minutes')
                : start.clone().hours(end.hours()).minutes(end.minutes());

            newState.endDate = newEnd.format(this._DATE_FORMAT);
            newState.endTime = newEnd.format(SHGlobals.DESKTOP_TIME_FORMAT);
            newState.addHighlightClass = true;
            isInvalid = true;

            this.setState(newState);
        }

        return isInvalid;
    }

    _checkIfEndIsBeforeStart() {
        const {
            startDate,
            startTime,
            endDate,
            endTime
        } = this.state;
        const start = this._getCombinedDate(startDate, startTime);
        const end = this._getCombinedDate(endDate, endTime);
        const newState = {};
        let isInvalid = false;

        if (end && (end.isBefore(start) || end.isSame(start))) {
            newState.addErrorClass = true;
            isInvalid = true;

            if (end.isBefore(start, 'day')) {
                newState.endTipMessage = 'Please choose an end date after your start date.';
                newState.endTipType = 'date';
            } else if (end.isBefore(start, 'hour') || end.isSame(start, 'hour')) {
                newState.endTipMessage = 'Please choose an end time after your start time.';
                newState.endTipType = 'time';
            }
        } else {
            newState.addErrorClass = false;
            newState.endTipMessage = null;
            newState.endTipType = 'none';
        }

        this.setState(newState);

        return isInvalid;
    }

    _startChange({selectedDate, selectedTime}) {
        const {
            onStartChange,
            controlled
        } = this.props;
        const {
            endDate,
            endTime
        } = this.state;

        if (!controlled) {
            this.setState({
                startDate: selectedDate,
                startTime: selectedTime
            });
        }

        if (onStartChange) {
            onStartChange({
                startDate: selectedDate,
                startTime: selectedTime,
                endDate,
                endTime
            });
        }
    }

    _endChange({selectedDate, selectedTime}) {
        const {
            onEndChange,
            controlled
        } = this.props;
        const {
            startDate,
            startTime
        } = this.state;

        if (!controlled) {
            this.setState({
                endDate: selectedDate,
                endTime: selectedTime
            });
        }

        if (onEndChange) {
            onEndChange({
                startDate,
                startTime,
                endDate: selectedDate,
                endTime: selectedTime
            });
        }
    }

    _startTooltipHidden() {
        this.setState({
            startTipType: 'none',
            startTipMessage: null
        });
    }

    _endTooltipHidden() {
        this.setState({
            endTipType: 'none',
            endTipMessage: null
        });
    }

    render() {
        const {
            className,
            useIcons,
            startDateLabel,
            startTimeLabel,
            endDateLabel,
            endTimeLabel,
            timeStep,
            tooltipPosition,
            updateTooltipPositionOnMove,
            disableStartPast,
            disableStartBefore,
            disableStartAfter,
            disableEndPast,
            disableEndBefore,
            disableEndAfter,
            useTetherShim,
            focusStartDate,
            focusStartTime,
            focusEndDate,
            focusEndTime,
            allFieldsEmpty
        } = this.props;
        const {
            startDate,
            startTime,
            endDate,
            endTime,
            addHighlightClass,
            addErrorClass,
            startTipType,
            startTipMessage,
            endTipType,
            endTipMessage
        } = this.state;
        const classes = classNames(
            'DateTimeRangePicker',
            className
        );
        const startClasses = classNames(
            'DateTimePicker-start',
            {'DateTimePicker-error': allFieldsEmpty}
        );
        const endClasses = classNames(
            'DateTimePicker-end',
            {'DateTimePicker-highlight': addHighlightClass},
            {'DateTimePicker-error': (addErrorClass || allFieldsEmpty)}
        );

        return (
            <div className={classes}>
                <DateTimePicker
                    className={startClasses}
                    calendarClassName="calendar-start"
                    useIcons={useIcons}
                    dateLabel={startDateLabel}
                    timeLabel={startTimeLabel}
                    timeStep={timeStep}
                    selectedDate={startDate}
                    selectedTime={startTime}
                    focusDate={focusStartDate}
                    focusTime={focusStartTime}
                    onChange={this._startChange}
                    tooltipType={startTipType}
                    tooltipMessage={startTipMessage}
                    tooltipPosition={tooltipPosition}
                    updateTooltipPositionOnMove={updateTooltipPositionOnMove}
                    onTooltipHidden={this._startTooltipHidden}
                    disablePast={disableStartPast}
                    disableBefore={disableStartBefore}
                    disableAfter={disableStartAfter}
                    useTetherShim={useTetherShim}
                    datePlaceholder="Start Date"
                    timePlaceholder="Start Time"
                />
                <DateTimePicker
                    className={endClasses}
                    calendarClassName="calendar-end"
                    useIcons={useIcons}
                    dateLabel={endDateLabel}
                    timeLabel={endTimeLabel}
                    timeStep={timeStep}
                    selectedDate={endDate}
                    selectedTime={endTime}
                    focusDate={focusEndDate}
                    focusTime={focusEndTime}
                    onChange={this._endChange}
                    tooltipType={endTipType}
                    tooltipMessage={endTipMessage}
                    tooltipPosition={tooltipPosition}
                    updateTooltipPositionOnMove={updateTooltipPositionOnMove}
                    onTooltipHidden={this._endTooltipHidden}
                    disablePast={disableEndPast}
                    disableBefore={disableEndBefore}
                    disableAfter={disableEndAfter}
                    useTetherShim={useTetherShim}
                    datePlaceholder="End Date"
                    timePlaceholder="End Time"
                />
            </div>
        );
    }
}

DateTimeRangePicker.propTypes = {
    className: PropTypes.string,
    useIcons: PropTypes.bool,
    startDateLabel: PropTypes.node,
    startTimeLabel: PropTypes.node,
    endDateLabel: PropTypes.node,
    endTimeLabel: PropTypes.node,
    timeStep: PropTypes.number,
    /** The amount of time (in minutes) the end time should bump up when the start date is after end date. Usually set to the same as timeStep. */
    endTimeBump: PropTypes.number,
    /** A string in the format 'MM/DD/YYYY' representing the start date which should be selected. */
    startDate: PropTypes.string,
    /** A string in the format of 'hh:mm A' representing the start time which should be selected. */
    startTime: PropTypes.string,
    /** A string in the format 'MM/DD/YYYY' representing the end date which should be selected. */
    endDate: PropTypes.string,
    /** A string in the format of 'hh:mm A' representing the end time which should be selected. */
    endTime: PropTypes.string,
    focusStartDate: PropTypes.bool,
    focusStartTime: PropTypes.bool,
    focusEndDate: PropTypes.bool,
    focusEndTime: PropTypes.bool,
    onStartChange: PropTypes.func,
    onEndChange: PropTypes.func,
    startTooltipType: PropTypes.oneOf(TOOLTIP_TYPES),
    endTooltipType: PropTypes.oneOf(TOOLTIP_TYPES),
    startTooltipMessage: PropTypes.string,
    endTooltipMessage: PropTypes.string,
    tooltipPosition: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
    updateTooltipPositionOnMove: PropTypes.bool,
    disableStartPast: PropTypes.bool,
    disableStartBefore: PropTypes.string,
    disableStartAfter: PropTypes.string,
    disableEndPast: PropTypes.bool,
    disableEndBefore: PropTypes.string,
    disableEndAfter: PropTypes.string,
    useTetherShim: PropTypes.bool,
    /** Are the datetime values being controlled by a parent component? */
    controlled: PropTypes.bool,
    allFieldsEmpty: PropTypes.bool,
    onInvalid: PropTypes.func
};

DateTimeRangePicker.defaultProps = {
    className: '',
    useIcons: true,
    startDateLabel: 'Start Date:',
    startTimeLabel: 'Start Time:',
    endDateLabel: 'End Date:',
    endTimeLabel: 'End Time:',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    timeStep: 30,
    endTimeBump: 30,
    startTooltipType: 'none',
    startTooltipMessage: null,
    endTooltipType: 'none',
    endTooltipMessage: null,
    tooltipPosition: 'bottom',
    updateTooltipPositionOnMove: false,
    disableStartPast: true,
    disableEndPast: true,
    controlled: false
};

export default DateTimeRangePicker;



// WEBPACK FOOTER //
// ./frontend/src/js/components/date-time-range-picker/DateTimeRangePicker.jsx